import { useEffect } from 'react'
import { Box, Flex } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { useIsMutating } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { goalFormSchema } from '@/modules/actionPlan/pages/components/GoalForm/GoalForm.schema'
import type {
  GoalFormProps,
  GoalFormSchema,
} from '@/modules/actionPlan/pages/components/GoalForm/GoalForm.types'

import { Button } from '@/components/Button/Button'
import { NumberInput } from '@/components/NumberInput/NumberInput'
import { SeparateDateInput } from '@/components/SeparateDateInput/SeparateDateInput'
import { Switch } from '@/components/Switch/Switch'
import { Text } from '@/components/Text/Text'
import { TextInput } from '@/components/TextInput/TextInput'
import { Title } from '@/components/Title/Title'

export function GoalForm({ data, edit, onClose, onSubmit }: GoalFormProps) {
  const form = useForm<GoalFormSchema>({
    initialValues: edit ? data : undefined,
    validate: zodResolver(goalFormSchema),
  })

  const isMutating = useIsMutating({
    exact: true,
  })

  const today = dayjs().toDate()

  const handleSubmit = form.onSubmit(values => onSubmit(values))

  const handleUpdateDate = (value: Date | undefined, key: string) => {
    form.setFieldValue(key, value ? value.toISOString() : '')
  }

  const handleSwitchChange = (event: {
    target: { checked: boolean | ((prevValue: boolean) => boolean) }
  }) => {
    form.setFieldValue('monthlyReminder', event.target.checked)
  }

  useEffect(() => {
    if (data && edit) {
      form.setValues(data)
      form.resetDirty(data)
    } else {
      form.setFieldValue('monthlyReminder', false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Flex align="center" direction="column">
        <Title mb={8} variant="h1">
          {edit ? 'Edit goal' : 'New goal'}
        </Title>

        <Text>
          We&apos;ll help you stay on track so you can achieve your goals!
        </Text>
      </Flex>

      <Box component="form" mt={32} noValidate onSubmit={handleSubmit}>
        <TextInput
          label="Name of goal"
          placeholder="Enter goal"
          {...form.getInputProps('name')}
        />

        <NumberInput
          hideControls
          label="Amount"
          placeholder="Enter amount"
          {...form.getInputProps('amount')}
        />

        <SeparateDateInput
          {...form.getInputProps('startDate')}
          label="Start date"
          minDate={!edit ? today : undefined}
          onChange={value => {
            if (value instanceof Date) {
              handleUpdateDate(value, 'startDate')

              if (
                form.values.dueDate &&
                dayjs(value).add(1, 'day').isAfter(form.values.dueDate)
              ) {
                handleUpdateDate(dayjs(value).add(1, 'day').toDate(), 'dueDate')
              }
            } else {
              handleUpdateDate(undefined, 'startDate')
              handleUpdateDate(undefined, 'dueDate')
            }
          }}
          value={
            form.values.startDate
              ? dayjs(form.values.startDate).toDate()
              : undefined
          }
        />

        <SeparateDateInput
          {...form.getInputProps('dueDate')}
          disabled={!form.values.startDate}
          label="Due date"
          minDate={dayjs(form.values.startDate).add(1, 'day').toDate()}
          onChange={value =>
            handleUpdateDate(
              value instanceof Date ? value : undefined,
              'dueDate',
            )
          }
          value={
            form.values.dueDate
              ? dayjs(form.values.dueDate).toDate()
              : undefined
          }
        />

        <Switch
          checked={form.values.monthlyReminder ?? false}
          label="Remind to set aside a certain amount every month"
          mb={16}
          onChange={handleSwitchChange}
        />

        {form.values.monthlyReminder && (
          <NumberInput
            hideControls
            label="Amount"
            placeholder="Enter amount"
            {...form.getInputProps('monthlyReminderAmount')}
          />
        )}

        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={16}
          justify={{ base: 'center', md: 'flex-start' }}
          mt={32}
        >
          <Button miw="48%" onClick={onClose} variant="outline">
            Cancel
          </Button>

          <Button loading={!!isMutating} miw="48%" type="submit">
            {!edit ? 'Create' : 'Save'}
          </Button>
        </Flex>
      </Box>
    </>
  )
}
